export type Point = [number, number]

const fixed = (value: number): string => (Math.round(value * 100) / 100).toString()

/**
 * Maps a series of values onto an SVG box (y grows downward).
 * `floor` lets a chart start its scale at zero instead of the minimum.
 */
export const scalePoints = (values: Array<number>, width: number, height: number, options: { pad?: number, floor?: number } = {}): Array<Point> => {
    const { pad = 0, floor } = options

    if (!values.length) return []

    const min = floor ?? Math.min(...values)
    const max = Math.max(...values)
    const span = max - min || 1
    const step = values.length > 1 ? (width - pad * 2) / (values.length - 1) : 0

    return values.map((value, index) => [
        pad + index * step,
        height - pad - ((value - min) / span) * (height - pad * 2)
    ])
}

const sign = (value: number): number => (value < 0 ? -1 : 1)

/**
 * Monotone cubic curve through the points (the same maths as d3's
 * curveMonotoneX), so a rising series never dips between data points.
 */
export const monotonePath = (points: Array<Point>): string => {
    const count = points.length

    if (count === 0) return ''
    if (count === 1) return `M${fixed(points[0][0])},${fixed(points[0][1])}`
    if (count === 2) return `M${fixed(points[0][0])},${fixed(points[0][1])} L${fixed(points[1][0])},${fixed(points[1][1])}`

    const dx: Array<number> = []
    const slopes: Array<number> = []

    for (let index = 0; index < count - 1; index++) {
        dx[index] = points[index + 1][0] - points[index][0]
        slopes[index] = dx[index] ? (points[index + 1][1] - points[index][1]) / dx[index] : 0
    }

    const tangents: Array<number> = new Array(count).fill(0)

    for (let index = 1; index < count - 1; index++) {
        const h0 = dx[index - 1]
        const h1 = dx[index]
        const s0 = slopes[index - 1]
        const s1 = slopes[index]
        const blended = (s0 * h1 + s1 * h0) / (h0 + h1)

        tangents[index] = (sign(s0) + sign(s1)) * Math.min(Math.abs(s0), Math.abs(s1), 0.5 * Math.abs(blended)) || 0
    }

    tangents[0] = (3 * slopes[0] - tangents[1]) / 2
    tangents[count - 1] = (3 * slopes[count - 2] - tangents[count - 2]) / 2

    let path = `M${fixed(points[0][0])},${fixed(points[0][1])}`

    for (let index = 0; index < count - 1; index++) {
        const [x0, y0] = points[index]
        const [x1, y1] = points[index + 1]
        const third = dx[index] / 3

        path += ` C${fixed(x0 + third)},${fixed(y0 + third * tangents[index])} ${fixed(x1 - third)},${fixed(y1 - third * tangents[index + 1])} ${fixed(x1)},${fixed(y1)}`
    }

    return path
}

export const areaPath = (points: Array<Point>, baseline: number): string => {
    if (points.length < 2) return ''

    const first = points[0]
    const last = points[points.length - 1]

    return `${monotonePath(points)} L${fixed(last[0])},${fixed(baseline)} L${fixed(first[0])},${fixed(baseline)} Z`
}
